// Client-only integrations come straight from the SDK: they don't exist in the
// server entry, so they can't live in the shared barrel's curated namespace.
import {
	browserProfilingIntegration,
	browserTracingIntegration,
	thirdPartyErrorFilterIntegration,
} from '@sentry/sveltekit';
import { Sentry, stampRenderingMode } from '$lib/services/sentry';
import { getEnvironment } from '$lib/utils/environment';
import {
	ABCMOUSE_ALLOW_URLS,
	getSentryConfig,
	TRACE_PROPAGATION_TARGETS,
} from '$lib/services/sentry/config';
import { AppInitializationService } from '$lib/services/app-initialization';
import { initCrossTabLogoutListener } from '$lib/services/auth';

const environment = getEnvironment();

Sentry.init({
	...getSentryConfig({
		environment,
		release: __RELEASE_NAME__ || 'local',
	}),
	integrations: [
		thirdPartyErrorFilterIntegration({
			// 'testing-key' (the old applicationKey) kept one release for cached bundles.
			filterKeys: ['abc-mkt-web-classic', 'testing-key'],
			behaviour: 'apply-tag-if-contains-third-party-frames',
		}),
		browserTracingIntegration({
			beforeStartSpan: stampRenderingMode,
			// Inert through the SvelteKit wrapper today (it starts navigation spans
			// itself, without the redirect flag), but pinned off so a future SDK
			// change can't silently fold the gateway's goto() into its pageload
			// and rewrite the abandonment baseline.
			detectRedirects: false,
			enableInp: true,
			// LoAF spans (10.69.0 @sentry/browser-utils): op 'ui.long-animation-frame', name
			// 'Main UI thread blocked'; script attribution lands in browser.script.invoker,
			// browser.script.invoker_type, code.filepath (source URL), code.function, and
			// browser.script.source_char_position.
			enableLongAnimationFrame: true,
			enableLongTask: false,
			// Default 1s idle gap ended transactions before the variant painted, truncating
			// durations and silently dropping late paint measurements (survivorship bias);
			// the SDK clamps the end-time to the last child span, so a longer idle window
			// doesn't inflate durations for routes that finish sooner than the timeout.
			idleTimeout: 5000,
		}),
		// No replayIntegration: statically importing it adds ~54KB gzip to the
		// critical bundle and error-rate > 0 puts every session into rrweb buffer
		// recording - both against the funnel-speed objective. Revisit as a
		// lazy-loaded follow-up (ABC2-19766).
		browserProfilingIntegration(),
	],
	// allowUrls filters error events by stack-frame origin; localhost frames would
	// never match, which would break the sentry_debug escape hatch.
	allowUrls: environment === 'local_development' ? undefined : ABCMOUSE_ALLOW_URLS,
	tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
});

export const handleError = Sentry.handleErrorWithSentry();

/**
 * SvelteKit client init hook. Runs once per hard pageload, before the first
 * route renders, and never on SPA navigations.
 * @returns {Promise<void>}
 */
export async function init() {
	initCrossTabLogoutListener();
	await AppInitializationService.initialize();
}
